/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import type { ToolbarGroupItem } from '@veeam/components/lib/Toolbar/types';

import {
    ColumnsStore,
    ContextMenuStore,
    DisabledStateStore,
    FiltersStore,
    NotificationStore,
    PaginationStore,
    RequestStore,
    SelectionStore,
    SortingStore,
    ExpandedRowsStore,
} from './stores';

import type { Filter, GridProps, GridStorageActions, PaginationState } from '../../interfaces';
import type { PaginationMode, SortingMode } from '../../enums';
import type { ToolbarItem } from '../../../Toolbar/interfaces';

export class GridStore<RowDataT, TotalRowDataT, FilterT> {
    selectionStore: SelectionStore<RowDataT, TotalRowDataT, FilterT>;
    notificationStore: NotificationStore<RowDataT, TotalRowDataT, FilterT>;
    filtersStore: FiltersStore<RowDataT, TotalRowDataT, FilterT>;
    sortingStore: SortingStore<RowDataT, TotalRowDataT, FilterT>;
    columnsStore: ColumnsStore<RowDataT, TotalRowDataT, FilterT>;
    paginationStore: PaginationStore<RowDataT, TotalRowDataT, FilterT>;
    requestStore: RequestStore<RowDataT, TotalRowDataT, FilterT>;
    disabledStateStore: DisabledStateStore<RowDataT, TotalRowDataT, FilterT>;
    contextMenuStore: ContextMenuStore;
    expandedRowsStore: ExpandedRowsStore<RowDataT, TotalRowDataT, FilterT>;

    constructor(props: GridProps<RowDataT, TotalRowDataT, FilterT>, gridStorageActions: GridStorageActions<FilterT> | null) {
        this.selectionStore = new SelectionStore<RowDataT, TotalRowDataT, FilterT>(this, props);
        this.notificationStore = new NotificationStore<RowDataT, TotalRowDataT, FilterT>(this, props);
        this.filtersStore = new FiltersStore<RowDataT, TotalRowDataT, FilterT>(this, props, gridStorageActions);
        this.sortingStore = new SortingStore<RowDataT, TotalRowDataT, FilterT>(this, props, gridStorageActions);
        this.columnsStore = new ColumnsStore<RowDataT, TotalRowDataT, FilterT>(this, props, gridStorageActions);
        this.paginationStore = new PaginationStore<RowDataT, TotalRowDataT, FilterT>(this, props);
        this.requestStore = new RequestStore<RowDataT, TotalRowDataT, FilterT>(this, props);
        this.disabledStateStore = new DisabledStateStore<RowDataT, TotalRowDataT, FilterT>(this, props);
        this.contextMenuStore = new ContextMenuStore();
        this.expandedRowsStore = new ExpandedRowsStore<RowDataT, TotalRowDataT, FilterT>(this, props, gridStorageActions);
    }

    goToPage = (page: number) => this.paginationStore.goToPage(page);

    applyFilters = (newFilters: Partial<Filter<FilterT>>) => this.filtersStore.applyFilters(newFilters);

    resetFilters = () => this.filtersStore.resetFilters();

    enable = () => this.disabledStateStore.enable();

    disable = () => this.disabledStateStore.disable();

    applySorting = (columnId: string) => this.sortingStore.applySorting(columnId);

    clear = () => this.paginationStore.clear();

    fetchData = () => this.requestStore.fetchData();

    updateData = (data: RowDataT[], total: number) => this.paginationStore.updateData({ data, meta: { pagingInfo: { total } } });

    reloadGrid = () => this.requestStore.reloadGrid();

    selectAll = () => this.selectionStore.selectAll();

    selectAllOnAllPages = () => this.selectionStore.selectAllOnAllPages();

    deselectAll = () => this.selectionStore.deselectAll();

    changeSortingMode = (sortingMode: SortingMode) => this.sortingStore.changeSortingMode(sortingMode);

    changePaginationMode = (paginationMode: PaginationMode) => this.paginationStore.changePaginationMode(paginationMode);

    appendContextMenu = (actions: ToolbarItem[], groups: ToolbarGroupItem[]) => this.contextMenuStore.appendContextMenu(actions, groups);

    onSelectionChange = (selected: RowDataT[]) => this.selectionStore.onSelectionChange(selected);

    setPagination = (paginationState: Partial<PaginationState>) => this.paginationStore.setPagination(paginationState);

    subscribeToUpdates = () => this.notificationStore.subscribeToUpdates();

    unsubscribeFromUpdates = () => this.notificationStore.unsubscribeFromUpdates();

    get loading() {
        return this.requestStore.loading;
    }

    set loading(value) {
        this.requestStore.setLoading(value);
    }

    get data() {
        return this.paginationStore.data;
    }

    get columns() {
        return this.columnsStore.columns;
    }

    get filters() {
        return this.filtersStore.filters;
    }

    get contextMenuActions() {
        return this.contextMenuStore.contextMenuActions;
    }

    get selected() {
        return this.selectionStore.selected;
    }

    set selected(selected: RowDataT[]) {
        this.selectionStore.setSelected(selected);
    }

    get selection() {
        return this.selectionStore.selection;
    }

    get selectionMode() {
        return this.selectionStore.selectionMode;
    }

    get sortRequestParams() {
        return this.sortingStore.sortRequestParams;
    }

    get requestParams() {
        return this.requestStore.requestParams;
    }

    get pagination() {
        return this.paginationStore.pagination;
    }

    get requestParamsForActions() {
        return this.requestStore.requestParamsForActions;
    }

    get requestRESTParamsForActions() {
        return this.requestStore.requestRESTParamsForActions;
    }

    get pageData() {
        return this.paginationStore.pageData;
    }

    get meta() {
        return this.expandedRowsStore.meta;
    }

    get sortingConfig() {
        return this.sortingStore.sortingConfig;
    }
}
